/**
 * The allocation drill — run → department family → store → article → size (bata-ui#148 step
 * 4.4, `docs/adr/6006`; the department level, bata-ui#166 W4-7, `docs/adr/6102`).
 *
 * `/inseason/allocation/:storeKey` is one store's page in one run, and
 * `/inseason/allocation/:storeKey/:articleKey` one article in that store. The keys are PATH
 * segments and the run stays `?runId=`, so every level is a link a planner can send
 * (`CLAUDE.md` §3.4): the path says WHAT is being looked at, the query says WHICH RUN it is
 * looked at in, and the run is never defaulted here any more than on the allocation screen.
 *
 * Everything in this file is pure — a string in, a value out — so the screens, the manifest's
 * export resolvers and the Cockpit (which links into the store level) share one spelling of
 * each path and the tests assert on strings rather than on `window`.
 */

import type { ProposalSummary, StockPositionSummary } from '@/api/generated';
import { sizeAxis, sizeName, type SizeName } from '@/planning';

import { groupProposals } from './proposal-groups';
import { RUN_ID_PARAM } from './scope';

/** The allocation screen — the level above the store. */
export const ALLOCATION_PATH = '/inseason/allocation';
/** The manifest's declared drill targets, verbatim — `drill-routes.test.ts` holds them to a route. */
export const STORE_DRILL_PATTERN = '/inseason/allocation/:storeKey';
export const ARTICLE_DRILL_PATTERN = '/inseason/allocation/:storeKey/:articleKey';

export interface AllocationDrill {
  readonly storeKey: string | null;
  readonly articleKey: string | null;
}

/**
 * The keys a pathname names. Pure; `null` for a level the path does not reach. A blank or
 * undecodable segment is not a key — a truncated link is not a request for the store `''`.
 */
export function readDrill(pathname: string): AllocationDrill {
  const segments = pathname.split('/').filter(Boolean);
  const base = ALLOCATION_PATH.split('/').filter(Boolean);
  const matchesBase = base.every((segment, index) => segments[index] === segment);
  if (!matchesBase) return { storeKey: null, articleKey: null };
  const storeKey = decodeSegment(segments[base.length]);
  return { storeKey, articleKey: storeKey === null ? null : decodeSegment(segments[base.length + 1]) };
}

/** `/inseason/allocation/<store>` — the key encoded, so a key with a slash stays one segment. */
export function storePath(storeKey: string): string {
  return `${ALLOCATION_PATH}/${encodeURIComponent(storeKey)}`;
}

/**
 * `?lines=served` — the store page's lines table narrowed to the lines that propose something, the
 * ones the «Proposal lines» card counts (ADR 9069). The dropped doors' markers stay in the table by
 * default (the client's minimum-size-run rule is shown, not hidden); only this link leaves them out.
 */
export const LINES_PARAM = 'lines';
export const LINES_SERVED = 'served';
export type LinesNarrowing = typeof LINES_SERVED | null;

/** The narrowing an address names; anything but `served` is every line. Pure. */
export function readLinesNarrowing(search: string): LinesNarrowing {
  return new URLSearchParams(search).get(LINES_PARAM) === LINES_SERVED ? LINES_SERVED : null;
}

/** `/inseason/allocation/<store>/<article>`. */
export function articlePath(storeKey: string, articleKey: string): string {
  return `${storePath(storeKey)}/${encodeURIComponent(articleKey)}`;
}

// ── the drill's own sub-pages (client rule 3, DEC-88, ADR 6006 amendment 2026-09-30) ───────

/**
 * The article's sales history, a page below the article: `…/<store>/<article>/sales-history`.
 * A fixed last segment under two parameters, so no route of fewer segments can capture it and
 * `readDrill` still reads the store and the article off it.
 */
export const SALES_HISTORY_SEGMENT = 'sales-history';
export const SALES_HISTORY_DRILL_PATTERN = `${ARTICLE_DRILL_PATTERN}/${SALES_HISTORY_SEGMENT}`;

export function salesHistoryPath(storeKey: string, articleKey: string): string {
  return `${articlePath(storeKey, articleKey)}/${SALES_HISTORY_SEGMENT}`;
}

/**
 * What one run read — its registry values, stock batch and forecast run (ADR 6121). Per RUN, not
 * per store, so a page of its own bounded by `?runId=` rather than a panel repeated on every
 * store and article page.
 */
export const RUN_PROVENANCE_PATH = '/inseason/run-provenance';

// ── the department level (bata-ui#166 W4-7, `docs/adr/6102`) ───────────────

/**
 * One department family in one run — `/inseason/departments/<family>?runId=`.
 *
 * Its own path prefix rather than a segment under `allocation/`, because `allocation/:storeKey`
 * already owns every second segment there: a store whose key were `departments` would be
 * shadowed, and `readDrill` would read a family as a store.
 */
export const DEPARTMENT_PATH = '/inseason/departments';
export const DEPARTMENT_DRILL_PATTERN = '/inseason/departments/:departmentFamily';

/**
 * The department a store or article page was reached THROUGH — `?department=`. It is the path
 * taken, not a filter: the store page stays the whole store, and the trail names the family as
 * the level above it so the way back is one click (ADR 6102).
 */
export const DEPARTMENT_PARAM = 'department';

/** `/inseason/departments/<family>` — the family encoded, so a slash stays one segment. */
export function departmentPath(family: string): string {
  return `${DEPARTMENT_PATH}/${encodeURIComponent(family)}`;
}

function decodeSegment(raw: string | undefined): string | null {
  if (raw === undefined) return null;
  try {
    const value = decodeURIComponent(raw).trim();
    return value === '' ? null : value;
  } catch {
    return null;
  }
}

/** The family a pathname names, or `null` when it is not a department page. Pure. */
export function readDepartment(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const base = DEPARTMENT_PATH.split('/').filter(Boolean);
  if (!base.every((segment, index) => segments[index] === segment)) return null;
  if (segments.length !== base.length + 1) return null;
  return decodeSegment(segments[base.length]);
}

/** The family a store or article page was reached through (`?department=`), or `null`. Pure. */
export function readDepartmentParam(search: string): string | null {
  const value = new URLSearchParams(search).get(DEPARTMENT_PARAM);
  return value === null || value.trim() === '' ? null : value;
}

/** The query every drill link carries: the run, the department it came through, nothing else. */
export function drillQuery(runId: string | null, department: string | null = null): Record<string, string> {
  return {
    ...(runId === null ? {} : { [RUN_ID_PARAM]: runId }),
    ...(department === null ? {} : { [DEPARTMENT_PARAM]: department }),
  };
}

/** A link's `href` — path plus the run — for an anchor that also works with a middle click. */
export function drillHref(path: string, runId: string | null, department: string | null = null): string {
  const query = new URLSearchParams(drillQuery(runId, department)).toString();
  return query === '' ? path : `${path}?${query}`;
}

/** The store page, optionally with its lines table narrowed (`?lines=served`), the run and department carried. */
export function storeLinesHref(storeKey: string, runId: string | null, department: string | null, narrowing: LinesNarrowing): string {
  return storeNarrowedHref(storeKey, runId, department, narrowing === null ? null : [LINES_PARAM, narrowing]);
}

/**
 * `?articles=served` — the store page's articles table narrowed to the articles with at least one line
 * that proposes something, the ones the «Articles» card counts (ADR 9069). An article whose only line is a
 * dropped door's marker stays in the table by default and is left out only by this link.
 */
export const ARTICLES_PARAM = 'articles';
export const ARTICLES_SERVED = 'served';
export type ArticlesNarrowing = typeof ARTICLES_SERVED | null;

/** The narrowing an address names; anything but `served` is every article. Pure. */
export function readArticlesNarrowing(search: string): ArticlesNarrowing {
  return new URLSearchParams(search).get(ARTICLES_PARAM) === ARTICLES_SERVED ? ARTICLES_SERVED : null;
}

/**
 * `?cover=none` — the store page's positions table narrowed to the positions with no cover figure, the
 * ones the «no cover figure» sentence counts (ADR 9069). Offered only when the page holds the store's
 * whole ledger: a first page of a longer one cannot list what the platform's summary counts.
 */
export const COVER_PARAM = 'cover';
export const COVER_NONE = 'none';
export type CoverNarrowing = typeof COVER_NONE | null;

/** The narrowing an address names; anything but `none` is every position. Pure. */
export function readCoverNarrowing(search: string): CoverNarrowing {
  return new URLSearchParams(search).get(COVER_PARAM) === COVER_NONE ? COVER_NONE : null;
}

/** A position with no cover figure (no weekly demand, or not reported): the histogram's «none» and the narrowed table's rows. */
export const hasNoCover = (row: Pick<StockPositionSummary, 'weeks_of_cover'>): boolean => row.weeks_of_cover === null;

/** The article page, optionally with its sizes table narrowed (`?lines=served`), the run and department carried. */
export function articleLinesHref(
  storeKey: string, articleKey: string, runId: string | null, department: string | null, narrowing: LinesNarrowing,
): string {
  const base = drillHref(articlePath(storeKey, articleKey), runId, department);
  if (narrowing === null) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${LINES_PARAM}=${narrowing}`;
}

/** The store page with one more parameter (a narrowing of one of its tables), the run and department carried. */
export function storeNarrowedHref(
  storeKey: string, runId: string | null, department: string | null, narrowing: readonly [string, string] | null,
): string {
  const base = drillHref(storePath(storeKey), runId, department);
  if (narrowing === null) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${narrowing[0]}=${narrowing[1]}`;
}

/**
 * Whether a link to `path` keeps the department it was reached through: only a store or an
 * article level does. The allocation screen and the department page itself are above it.
 */
export function carriesDepartment(path: string): boolean {
  return readDrill(path.split('?')[0] ?? '').storeKey !== null;
}

/** One store's lines inside one department family, rolled up for the department page. */
export interface DepartmentStoreRow {
  readonly storeKey: string;
  readonly lines: number;
  readonly articles: number;
  readonly units: number;
  readonly requestedUnits: number;
  readonly unmetUnits: number;
}

/** The department's proposals, one row per store, most units first (ties by key, stable). */
export function departmentStores(lines: readonly ProposalSummary[]): readonly DepartmentStoreRow[] {
  // The chapter's one aggregation (`./proposal-groups.ts`, ADR 6020), as the store view uses it.
  return groupProposals(lines, 'store').map((group) => ({
    storeKey: group.id,
    lines: group.lines,
    articles: group.articles,
    units: group.units,
    requestedUnits: group.requested,
    unmetUnits: group.uncovered,
  }));
}

/**
 * Sizes in scale order (design/09 §12.11): the platform's `size_run_position` (bata-platform
 * ADR 2006, bata-ui ADR 9027), one per `size_key`, an unnamed size after every named one. The
 * key stays the join id; the order and the label come from the name, never from the key's
 * spelling — an MD5 sorts nothing a planner would recognise.
 */
export function sizeOrder(sizes: Iterable<SizeName>): readonly SizeName[] {
  return sizeAxis(sizes);
}

/** One article's lines in a store, rolled up for the store page's article table. */
export interface StoreArticleRow {
  readonly articleKey: string;
  readonly lines: number;
  readonly sizes: number;
  readonly units: number;
  readonly requestedUnits: number;
  /** Σ max(0, requested − units) per line — the digest's own definition (bata-platform ADR 6039). */
  readonly unmetUnits: number;
  /** The part of `requestedUnits` that is a dropped door's need (`./dropped-door.ts`): pairs no size run could carry. */
  readonly droppedUnits: number;
}

/** Unmet need on one line, never negative: a line proposing more than it asked meets it. */
export const unmetOf = (line: Pick<ProposalSummary, 'units' | 'requested_units'>): number =>
  Math.max(0, line.requested_units - line.units);

/** The store's proposals, one row per article, most units first (ties by key, stable). */
export function storeArticles(lines: readonly ProposalSummary[]): readonly StoreArticleRow[] {
  // The chapter's one aggregation (`./proposal-groups.ts`, ADR 6020): the Proposals page's article
  // view and its side panel add the same lines up with the same function.
  return groupProposals(lines, 'article').map((group) => ({
    articleKey: group.id,
    lines: group.lines,
    sizes: group.sizes,
    units: group.units,
    requestedUnits: group.requested,
    unmetUnits: group.uncovered,
    droppedUnits: group.dropped ?? 0,
  }));
}

/**
 * The fill rate over some lines — ADR 9015's Σ`units` / Σ`requested_units` — or `null` when
 * nothing was requested. Never 0 for an absence: lines that asked for nothing were not filled
 * at 0%, they posed no question (the digest's `nothing_requested`).
 */
export function fillRate(lines: readonly Pick<ProposalSummary, 'units' | 'requested_units'>[]): number | null {
  const requested = lines.reduce((sum, line) => sum + line.requested_units, 0);
  if (requested <= 0) return null;
  return lines.reduce((sum, line) => sum + line.units, 0) / requested;
}

/** How many articles the store heatmap draws — the most units first; the table carries all. */
export const HEATMAP_ARTICLES = 12;

/**
 * The store's proposed units as article × size: one row per article (the `HEATMAP_ARTICLES`
 * with the most units), one column per size in scale order. A size the article has no line
 * for in this store is `not-ranged` for the PROPOSAL — the run proposed nothing there — and
 * never a 0, which would say the run proposed nothing where it did look.
 */
export function proposalHeatmap(lines: readonly ProposalSummary[], limit = HEATMAP_ARTICLES): {
  readonly sizes: readonly SizeName[];
  readonly rows: readonly { readonly articleKey: string; readonly data: readonly (number | { readonly missing: 'not-ranged' })[] }[];
  readonly omitted: number;
} {
  const articles = storeArticles(lines);
  const shown = articles.slice(0, limit);
  const kept = new Set(shown.map((row) => row.articleKey));
  const sizes = sizeOrder(lines.filter((line) => kept.has(line.article_key)).map((line) => sizeName(line)));
  const cells = new Map<string, number>();
  for (const line of lines) {
    if (!kept.has(line.article_key)) continue;
    const key = `${line.article_key}\u0000${line.size_key}`;
    cells.set(key, (cells.get(key) ?? 0) + line.units);
  }
  return {
    sizes,
    rows: shown.map((row) => ({
      articleKey: row.articleKey,
      data: sizes.map((size) => cells.get(`${row.articleKey}\u0000${size.key}`) ?? { missing: 'not-ranged' as const }),
    })),
    omitted: articles.length - shown.length,
  };
}

/**
 * Weeks of cover for one store, in fixed bins — the store page's histogram. The positions read
 * publishes `weeks_of_cover` per store × article × size; `null` (no weekly demand) is counted
 * apart and never binned as 0, which would draw "no demand" as "no cover".
 */
export const COVER_BIN_EDGES = [0, 1, 2, 4, 8, 13] as const;

export function coverBins(positions: readonly StockPositionSummary[]): {
  readonly counts: readonly number[];
  readonly noDemand: number;
} {
  const counts: number[] = COVER_BIN_EDGES.map(() => 0);
  let noDemand = 0;
  for (const row of positions) {
    const cover = row.weeks_of_cover;
    if (hasNoCover(row) || cover === null) {
      noDemand += 1;
      continue;
    }
    let bin = 0;
    for (let i = 0; i < COVER_BIN_EDGES.length; i += 1) {
      if (cover >= (COVER_BIN_EDGES[i] ?? 0)) bin = i;
    }
    counts[bin] = (counts[bin] ?? 0) + 1;
  }
  return { counts, noDemand };
}

/**
 * The positions of one store (and, when given, one article) out of what the read returned.
 *
 * The request already carries `store_key` (and `article_key`), and platform v0.48 honours them,
 * so there this is a no-op. It stays because v0.47 — production until v0.48 deploys — ignores a
 * query parameter it does not know and answers with the whole run's positions: without this, the
 * store page would show every store's rows as this store's. Correct on both versions; remove it
 * once no deployed platform predates bata-platform#429.
 */
export function keepDrill(
  rows: readonly StockPositionSummary[],
  storeKey: string | null,
  articleKey: string | null,
): StockPositionSummary[] {
  return rows.filter((row) => row.store_key === storeKey && (articleKey === null || row.article_key === articleKey));
}
